// Public legal page required by the Google Play Console for the
// BBMI-OAI app's Data Safety form. Lives at /account-deletion and
// works without authentication so Play reviewers, end-users without
// the app, and search engines can all reach it directly.
//
// Linked from Google Play Console → App content → Data safety →
// "Account deletion link" and "Data deletion link".
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, AlertCircle } from 'lucide-react';
import { LOGO_SMALL } from '../components/landing/constants';

const MAILTO_HREF =
  'mailto:dpetrov@kaizenkanri.nl' +
  '?subject=' + encodeURIComponent('Delete my account — BMI Digitaal Logboek') +
  '&body=' + encodeURIComponent(
    'Please delete my account and all personal data.\n\n' +
    'Account email: \n' +
    'Company name: \n'
  );

const AccountDeletionPage = () => (
  <div className="min-h-screen bg-[#0a0a0a] text-white">
    {/* Slim header — links back to home + brand mark so reviewers
        understand which company controls this notice. */}
    <header className="border-b border-white/[0.06] bg-[#0a0a0a]/90 backdrop-blur sticky top-0 z-10">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="back-to-home">
          <ArrowLeft className="h-4 w-4 text-white/40 group-hover:text-[#00CED1] transition-colors" />
          <span className="text-sm text-white/50 group-hover:text-white transition-colors">Back to kaizenkanri.nl</span>
        </Link>
        <img src={LOGO_SMALL} alt="Kaizen Kanri" className="h-9 w-auto rounded" />
      </div>
    </header>

    <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
      <div className="flex items-center gap-2 mb-3">
        <span className="h-1 w-8 bg-[#00CED1] rounded-full" />
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00CED1]">Legal &middot; Compliance</span>
      </div>
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2">Account &amp; Data Deletion</h1>
      <p className="text-white/45 text-sm mb-10">
        Brandmeldinstallatie Digitaal Logboek (BBMI-OAI) &middot; by Kaizen Kanri
      </p>

      <p className="text-white/75 leading-relaxed text-[17px] mb-10">
        You can delete your account and the personal data associated with it in the{' '}
        <strong className="text-white">Brandmeldinstallatie Digitaal Logboek</strong> app
        (developer: <strong className="text-white">Kaizen Kanri</strong>) at any time. There are
        two ways to do this.
      </p>

      {/* ─────────────────────── Option 1 ─────────────────────── */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold mb-3">
          <span className="text-[#00CED1] mr-2">Option 1</span>
          <span className="text-white">Delete inside the app (instant)</span>
        </h2>
        <ol className="list-decimal pl-6 space-y-1.5 text-white/70 marker:text-[#00CED1] marker:font-semibold">
          <li>Open the app and sign in.</li>
          <li>Go to <strong className="text-white">Profile</strong>.</li>
          <li>Scroll to <strong className="text-white">Danger Zone</strong> and tap <strong className="text-white">&ldquo;Delete my account&rdquo;</strong>.</li>
          <li>Confirm. Your account and personal data are removed immediately.</li>
        </ol>
        <p className="mt-4 text-[13px] text-white/40 leading-relaxed flex items-start gap-2">
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-amber-400/70" />
          <span>
            If you are the last administrator of a company workspace, you will be asked to
            make another team member an administrator first, so shared company records are
            not orphaned.
          </span>
        </p>
      </section>

      {/* ─────────────────────── Option 2 ─────────────────────── */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold mb-3">
          <span className="text-[#00CED1] mr-2">Option 2</span>
          <span className="text-white">Request deletion by email (no app needed)</span>
        </h2>
        <p className="text-white/70 mb-5 leading-relaxed">
          Don&rsquo;t have the app installed? Send us a deletion request and we will verify
          ownership and complete it within <strong className="text-white">30 days</strong>,
          then confirm by email.
        </p>
        <div className="rounded-xl border border-[#00CED1]/20 bg-[#00CED1]/[0.04] p-5 sm:p-6">
          <a
            href={MAILTO_HREF}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#00CED1] hover:bg-[#00b4b7] text-[#0a0a0a] font-semibold text-sm transition-colors"
            data-testid="account-deletion-email-cta"
          >
            <Mail className="h-4 w-4" />
            Request account deletion by email
          </a>
          <p className="mt-4 text-sm text-white/55 leading-relaxed">
            Or email us directly at{' '}
            <a href="mailto:dpetrov@kaizenkanri.nl" className="text-[#00CED1] hover:underline">
              dpetrov@kaizenkanri.nl
            </a>{' '}
            with the subject &ldquo;Delete my account&rdquo;. Include your{' '}
            <strong className="text-white">account email</strong> and{' '}
            <strong className="text-white">company name</strong> so we can verify ownership.
          </p>
        </div>
      </section>

      {/* ───────────────────── What is deleted ─────────────────── */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold mb-3 text-white">What data is deleted</h2>
        <ul className="list-disc pl-6 space-y-1.5 text-white/70 marker:text-[#00CED1]">
          <li>Your account profile (name, email, role)</li>
          <li>Buildings, fire-alarm installations, zones, loops and inspection logs you created</li>
          <li>Uploaded photos and fault reports</li>
          <li>Diagnostic / usage data linked to your account</li>
        </ul>
      </section>

      {/* ─────────────────────── What is retained ──────────────── */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold mb-3 text-white">What data may be retained</h2>
        <p className="text-white/70 leading-relaxed">
          We may retain a limited set of records only where required by law &mdash; for example,
          invoices and payment records needed for tax and accounting obligations (typically up
          to 7 years under EU/NL law). These are kept solely for legal compliance and are not
          used for any other purpose.
        </p>
      </section>

      <div className="mt-10 pt-6 border-t border-white/[0.06]">
        <p className="text-sm text-white/45">
          Questions? Contact us at{' '}
          <a href="mailto:dpetrov@kaizenkanri.nl" className="text-[#00CED1] hover:underline">
            dpetrov@kaizenkanri.nl
          </a>
          .
        </p>
        <p className="mt-4 text-[11px] text-white/30">
          Kaizen Kanri &middot; KVK 42049431 &middot; The Netherlands
        </p>
      </div>
    </main>
  </div>
);

export default AccountDeletionPage;
